import { Injectable, Provider } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormRecord } from '@angular/forms';
import { SyllabusQuery } from '@examdojo/category';
import { ChallengeActivityQuery } from '@examdojo/challenge';
import { shareOneReplay } from '@examdojo/core/rxjs';
import { isNotNullish } from '@examdojo/core/util/nullish';
import { MarkCalculationMethod } from '@examdojo/models/markscheme-v2';
import { StemCoordinates } from '@examdojo/models/question';
import {
  getContentJsonPathFromCoordinates,
  GradedQuestionItemV2,
  GradedStemGroupItemV2,
  GradedStemItemV2,
  QuestionAttemptEntityQuery,
  QuestionGradingPayload,
  SelfGradingFormRecord,
  SelfGradingSelectionData,
  SelfGradingSelectionService,
} from '@examdojo/question';
import {
  BehaviorSubject,
  combineLatest,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  merge,
  switchMap,
  tap,
} from 'rxjs';

@Injectable()
export class MultiAttemptSelfGradingSelectionService extends SelfGradingSelectionService {
  constructor(
    syllabusQuery: SyllabusQuery,
    questionAttemptQuery: QuestionAttemptEntityQuery,
    private readonly challengeActivityQuery: ChallengeActivityQuery,
  ) {
    super(syllabusQuery, questionAttemptQuery);

    merge(this.resetOnChallengeActivityChange(), this.resetFormOnQuestionAttemptChange(), this.populateForm())
      .pipe(takeUntilDestroyed())
      .subscribe();
  }

  override readonly form: SelfGradingFormRecord = new FormRecord<FormRecord<FormControl<string[]>>>({});

  private readonly selectionDataPerAttempt$$ = new BehaviorSubject<Map<string, SelfGradingSelectionData>>(new Map());
  private readonly hintDismissedPerAttempt$$ = new BehaviorSubject<Set<string>>(new Set());

  readonly dismissedHints$ = this.hintDismissedPerAttempt$$.asObservable();

  readonly selfGradingResultsPerAttempt$ = combineLatest([
    this.selectionDataPerAttempt$$,
    this.hintDismissedPerAttempt$$,
  ]).pipe(
    map(([selectionData, dismissedHints]) => {
      const results = new Map<string, { awardedMarks: number }>();

      dismissedHints.forEach((attemptId) => {
        const selection = selectionData.get(attemptId);
        const entity = this.questionAttemptQuery.getUIEntity(attemptId);

        if (!selection || !entity?.questionItems?.length || !entity.markCalculationMethod) {
          return;
        }

        const awardedMarks = this.calculateAwardedMarksFromSelection(entity, selection);
        results.set(attemptId, { awardedMarks });
      });

      return results;
    }),
    shareOneReplay(),
  );

  override readonly isHintDismissed$ = this.questionAttemptQuery
    .selectActiveId()
    .pipe(
      switchMap((activeId) =>
        this.hintDismissedPerAttempt$$.pipe(map((set) => (activeId ? set.has(activeId) : false))),
      ),
    );

  override dismissHint(): void {
    const activeId = this.questionAttemptQuery.getActiveId();
    if (activeId) {
      const updated = new Set(this.hintDismissedPerAttempt$$.value);
      updated.add(activeId);
      this.hintDismissedPerAttempt$$.next(updated);
    }
  }

  getSelectionDataForAttempt(attemptId: string): SelfGradingSelectionData | undefined {
    return this.selectionDataPerAttempt$$.value.get(attemptId);
  }

  getAllSelectionData(): Map<string, SelfGradingSelectionData> {
    return this.selectionDataPerAttempt$$.value;
  }

  generateAllGradingPayloads(): Array<{ attemptId: string; payload: QuestionGradingPayload }> {
    return Array.from(this.selectionDataPerAttempt$$.value.entries())
      .map(([attemptId, selectionData]) => {
        const items = this.questionAttemptQuery.getUIEntity(attemptId)?.questionItems ?? [];
        if (items.length === 0) {
          return null;
        }
        return { attemptId, payload: this.populateQuestionItemsWithSelfGradingResult(items, selectionData) };
      })
      .filter((item) => item !== null);
  }

  private resetFormOnQuestionAttemptChange() {
    return this.questionAttemptQuery.selectActiveId().pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      tap(() => this.resetForm(this.form)),
    );
  }

  private populateForm() {
    return this.questionAttemptQuery.active$.pipe(
      filter(isNotNullish),
      tap((currentAttempt) => {
        const items = currentAttempt.question?.question_items ?? [];

        if (items.length === 0) {
          return;
        }

        if (!this.questionAttemptQuery.isSelfGradingAllowed(currentAttempt.gradingError)) {
          return;
        }

        const stemSolutionPaths = this.collectStemSolutionPaths(items);
        const savedSelection = this.selectionDataPerAttempt$$.value.get(currentAttempt.id);

        this.populateFormFromStemSolutionPaths(this.form, stemSolutionPaths, savedSelection);
      }),
      switchMap((currentAttempt) => this.syncFormToSelectionData(currentAttempt.id)),
    );
  }

  private syncFormToSelectionData(attemptId: string) {
    return this.form.valueChanges.pipe(
      debounceTime(0),
      tap((formValue) => {
        if (attemptId !== this.questionAttemptQuery.getActiveId() || !isNotNullish(formValue)) {
          return;
        }

        const updated = new Map(this.selectionDataPerAttempt$$.value);
        updated.set(attemptId, formValue satisfies SelfGradingSelectionData);
        this.selectionDataPerAttempt$$.next(updated);
      }),
    );
  }

  private calculateAwardedMarksFromSelection(
    entity: { questionItems: GradedQuestionItemV2[]; markCalculationMethod: MarkCalculationMethod },
    selectionData: SelfGradingSelectionData,
  ): number {
    const allCoordinates = this.getAllStemCoordinatesFromItems(entity.questionItems);

    return allCoordinates.reduce((sum, coordinates) => {
      const stem = this.getStemByCoordinatesFromItems(entity.questionItems, coordinates);
      if (!stem?.markscheme) {
        return sum;
      }

      const stemKey = getContentJsonPathFromCoordinates(coordinates);
      const stemSelection = selectionData[stemKey];
      if (!stemSelection) {
        return sum;
      }

      const selectedIdsPerPath = stem.markscheme.solution_paths.map((_, pathIndex) => {
        return stemSelection[`solution-path-${pathIndex}`] ?? [];
      });

      return (
        sum +
        this.questionAttemptQuery.calculateSelfGradingMarksForStem(
          stem,
          selectedIdsPerPath,
          entity.markCalculationMethod,
        )
      );
    }, 0);
  }

  private getAllStemCoordinatesFromItems(items: GradedQuestionItemV2[]): StemCoordinates[] {
    const coordinates: StemCoordinates[] = [];
    items.forEach((item, rootItemIndex) => {
      if (item.type === 'stem') {
        coordinates.push({ rootItemIndex });
      } else if (item.type === 'stem_group') {
        item.stems.forEach((_, stems) => {
          coordinates.push({ rootItemIndex, stems });
        });
      }
    });
    return coordinates;
  }

  private getStemByCoordinatesFromItems(
    items: GradedQuestionItemV2[],
    coordinates: StemCoordinates,
  ): GradedStemItemV2 | null {
    const item = items[coordinates.rootItemIndex];
    if (!item) {
      return null;
    }
    if (item.type === 'stem') {
      return item;
    }
    if (item.type === 'stem_group' && coordinates.stems !== undefined) {
      return (item as GradedStemGroupItemV2).stems[coordinates.stems] ?? null;
    }
    return null;
  }

  private resetOnChallengeActivityChange() {
    return this.challengeActivityQuery.inProgressChallengeActivityId$.pipe(
      filter(isNotNullish),
      distinctUntilChanged(),
      tap(() => {
        this.resetForm(this.form);
        this.selectionDataPerAttempt$$.next(new Map());
        this.hintDismissedPerAttempt$$.next(new Set());
      }),
    );
  }
}

export function provideMultiAttemptSelfGradingSelection(): Provider[] {
  return [
    MultiAttemptSelfGradingSelectionService,
    { provide: SelfGradingSelectionService, useExisting: MultiAttemptSelfGradingSelectionService },
  ];
}
